import { defineComponent as _defineComponent } from 'vue'
import { useCustomerReviewStore } from "@/store/modules/customerReview";
import StepwiseProgress from "../StepwiseProgress.vue";
import TripCard from "../TripCard.vue";
import TripCustomerFeedback2Field from "./TripCustomerFeedback2Field.vue";
import CUItinerarySkeletonLoader from "@/components/CUItinerarySkeletonLoader.vue";
import NotFoundLayout from "@/layouts/NotFoundLayout.vue";
import thanksGraphic from "@/assets/images/tripCustomerReview/thanksGraphic.svg";
import googleGraphic from "@/assets/images/tripCustomerReview/googleRating.png";
import i18n from "@/plugins/i18n";

import { computed, ref, onMounted, onBeforeUnmount, watch } from "vue";


export default /*#__PURE__*/_defineComponent({
  __name: 'TripCustomerFeedback2',
  props: {
  reservationId: {
    type: String,
    required: true,
  },
  customerId: {
    type: String,
    required: true,
  },
  initialStars: {
    type: Number,
    default: null,
  },
},
  setup(__props) {

const props = __props;



const redirectTimer = ref<number | null>(null);
const GOOGLE_REVIEW_URL = 'https://search.google.com/local/writereview?placeid=ChIJe0x_h2MP9YgREF3VOKTU5W0';

const dataStore = useCustomerReviewStore();

onMounted(async () => {
  await dataStore.fetchReviewDetails(props.reservationId, props.customerId);
  if (props.initialStars) {
    dataStore.setInitialRating(props.initialStars);
  }
});

const progressSteps = ref<Array<{ title: string }>>([
  { title: i18n.t("reviewsV2.STEP_OVERALL").toString() },
  { title: i18n.t("reviewsV2.STEP_DRIVER").toString() },
  { title: i18n.t("reviewsV2.STEP_VEHICLE").toString() },
]);

const driverImprovementOptions = [
  {
    text: i18n.t("reviewsV2.DRIVER_IMPROVEMENT_OPTIONS.TIMELINESS").toString(),
    value: "TIMELINESS",
  },
  {
    text: i18n.t("reviewsV2.DRIVER_IMPROVEMENT_OPTIONS.PROFESSIONALISM").toString(),
    value: "PROFESSIONALISM",
  },
  {
    text: i18n.t("reviewsV2.DRIVER_IMPROVEMENT_OPTIONS.SAFETY").toString(),
    value: "SAFETY",
  },
  {
    text: i18n.t("reviewsV2.DRIVER_IMPROVEMENT_OPTIONS.ROUTE_CHOICE").toString(),
    value: "ROUTE_CHOICE",
  },
];

const vehicleImprovementOptions = [
  {
    text: i18n.t("reviewsV2.VEHICLE_IMPROVEMENT_OPTIONS.CONDITION").toString(),
    value: "CONDITION"
  },
  {
    text: i18n.t("reviewsV2.VEHICLE_IMPROVEMENT_OPTIONS.CLEANLINESS").toString(),
    value: "CLEANLINESS"
  },
  {
    text: i18n.t("reviewsV2.VEHICLE_IMPROVEMENT_OPTIONS.AMENITIES").toString(),
    value: "AMENITIES"
  }
];

const currentStepIdx = computed(() => dataStore.currentStepIdx);

const goNext = async () => {
  if (canGoNext) {
    dataStore.updateReview(props.reservationId, props.customerId);
  }
};

const goPrev = () => {
  if (canGoBack) {
    dataStore.updateCurrentStepIdx(currentStepIdx.value - 1);
  }
};

const nextText = computed(() => {
  switch (currentStepIdx.value) {
    case 0:
      return i18n.t("reviewsV2.STEP_OVERALL_BUTTON");
    case 1:
      return i18n.t("reviewsV2.STEP_DRIVER_BUTTON");
    case 2:
      return i18n.t("reviewsV2.STEP_VEHICLE_BUTTON");
    default:
      return "Next";
  }
});

const canGoBack = computed(() => {
  return currentStepIdx.value > 0;
});

const canGoNext = computed(() => {
  switch (currentStepIdx.value) {
    case 0:
      return dataStore.overallRating > 0;
    case 1:
      return dataStore.driverReviews.every((dr) => dr?.rating > 0) || (dataStore.useOverallDriverReview && dataStore.overallDriverRating > 0);
    case 2:
      return dataStore.vehicleReviews.every((vr) => vr?.rating > 0) || (dataStore.useOverallVehicleReview && dataStore.overallVehicleRating > 0);
    default:
      return false;
  }
});

watch(
  () => currentStepIdx.value > 3 && dataStore.userShouldRedirectToGoogle,
  (shouldRedirect) => {
    if (shouldRedirect) {
      if (redirectTimer.value) {
        clearTimeout(redirectTimer.value);
      }
      redirectTimer.value = window.setTimeout(() => {
        window.location.href = GOOGLE_REVIEW_URL;
      }, 4000);
    }
  }
);

onBeforeUnmount(() => {
  if (redirectTimer.value) {
    clearTimeout(redirectTimer.value);
  }
});

const handleUpdate = (item: string, value) => {
  if (item === "overall") {
    dataStore.overallRating = value.rating;
    if (value.rating === 5) {
      dataStore.driverReviews.forEach((review) => {
        review.rating = 5;
        review.driverImprovementAttributes = [];
        review.comment = "";
      });
      dataStore.vehicleReviews.forEach((review) => {
        review.rating = 5;
        review.vehicleImprovementAttributes = [];
        review.comment = "";
      });
    }
  } else if (item === "vehicle_overall") {
    dataStore.overallVehicleRating = value.rating;
    dataStore.overallVehicleComment = value.feedback;
    dataStore.overallVehicleImprovementAttributes = value.checkboxes;
  } else if (item === "driver_overall") {
    dataStore.overallDriverRating = value.rating;
    dataStore.overallDriverComment = value.feedback;
    dataStore.overallDriverImprovementAttributes = value.checkboxes;
  } else if (item.includes("driver")) {
    const driverIndex = parseInt(item.split("_")[1]);
    if (driverIndex !== -1) {
      dataStore.driverReviews[driverIndex].rating = value.rating;
      dataStore.driverReviews[driverIndex].comment = value.feedback;
      dataStore.driverReviews[driverIndex].driverImprovementAttributes = value.checkboxes;
    }
  } else if (item.includes("vehicle")) {
    const vehicleIndex = parseInt(item.split("_")[1]);
    if (vehicleIndex !== -1) {
      dataStore.vehicleReviews[vehicleIndex].rating = value.rating;
      dataStore.vehicleReviews[vehicleIndex].comment = value.feedback;
      dataStore.vehicleReviews[vehicleIndex].vehicleImprovementAttributes = value.checkboxes;
    }
  }
};

const handleBackClick = () => {
  goPrev();
};

const handleNextClick = () => {
  goNext();
};


return { __sfc: true,props, redirectTimer, GOOGLE_REVIEW_URL, dataStore, progressSteps, driverImprovementOptions, vehicleImprovementOptions, currentStepIdx, goNext, goPrev, nextText, canGoBack, canGoNext, handleUpdate, handleBackClick, handleNextClick, StepwiseProgress, TripCard, TripCustomerFeedback2Field, CUItinerarySkeletonLoader, NotFoundLayout, thanksGraphic, googleGraphic }
}

})